/** @format */

import React, { useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom"; // Added useNavigate
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import {
	Calendar,
	Clock,
	ArrowLeft,
	ArrowRight,
	Tag,
	Share2,
	Link as LinkIcon,
	Facebook,
	Twitter,
	Linkedin,
	Printer,
	AlertCircle,
} from "lucide-react"; // Added more icons

// Data & Components (Ensure paths are correct)
import posts from "../../blog-posts";
import MarkdownPost from "../../components/MarkdownPost"; // Assuming this component renders post.text (markdown)
import { LoadingSpinner } from "../../components/LoadingSpinner"; // Assuming path

// --- Reusable Related Post Card ---
const RelatedPostCard = ({ post }) => (
	<Link
		to={`/blog/${post.id}`}
		className="group block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:border-primary/20 transition-colors duration-200 h-full flex flex-col"
	>
		<div className="aspect-video overflow-hidden bg-gray-100">
			<img
				src={post.image}
				alt={post.title}
				className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500 ease-in-out"
				loading="lazy"
			/>
		</div>
		<div className="p-4 flex flex-col flex-grow">
			<h3 className="font-semibold text-sm text-gray-900 group-hover:text-primary transition-colors mb-1 leading-snug">
				{post.title}
			</h3>
			{/* Optional: Show excerpt for related posts? */}
			{/* <p className="text-xs text-gray-600 line-clamp-2 flex-grow">{post.text}</p> */}
			<span className="mt-auto text-xs text-primary group-hover:underline">
				Read More →
			</span>
		</div>
	</Link>
);

// --- Simple Social Share Component ---
const SocialShare = ({ url, title }) => {
	const encodedUrl = encodeURIComponent(url);
	const encodedTitle = encodeURIComponent(title);

	const networks = [
		{
			name: "Facebook",
			icon: Facebook,
			url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
		},
		{
			name: "Twitter",
			icon: Twitter,
			url: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
		},
		{
			name: "LinkedIn",
			icon: Linkedin,
			url: `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedTitle}`,
		},
	];

	const copyLink = () => {
		navigator.clipboard
			.writeText(url)
			.then(() => {
				alert("Link copied to clipboard!"); // Replace with a toast later
			})
			.catch((err) => {
				console.error("Failed to copy link: ", err);
				alert("Failed to copy link.");
			});
	};

	return (
		<div className="flex items-center gap-2">
			<span className="text-xs font-medium text-gray-500">Share:</span>
			{networks.map((net) => (
				<a
					key={net.name}
					href={net.url}
					target="_blank"
					rel="noopener noreferrer"
					title={`Share on ${net.name}`}
					className="p-1.5 text-gray-500 hover:text-primary transition-colors rounded-full hover:bg-primary/10"
				>
					<net.icon size={16} />
				</a>
			))}
			<button
				onClick={copyLink}
				title="Copy link"
				className="p-1.5 text-gray-500 hover:text-primary transition-colors rounded-full hover:bg-primary/10"
			>
				<LinkIcon size={16} />
			</button>
			{/* Optional Print Button */}
			{/* <button onClick={() => window.print()} title="Print article" className="p-1.5 text-gray-500 hover:text-primary transition-colors rounded-full hover:bg-primary/10"> <Printer size={16} /> </button> */}
		</div>
	);
};

// --- Main Blog Post Component ---
export default function BlogPost() {
	const { id } = useParams();
	const navigate = useNavigate();

	// --- Find Post and Navigation ---
	// Memoize post finding and navigation links
	const { post, currentIndex, prevPost, nextPost, relatedPosts } =
		useMemo(() => {
			const currentIdx = posts.findIndex((p) => p.id.toString() === id);
			if (currentIdx === -1) {
				return {
					post: null,
					currentIndex: -1,
					prevPost: null,
					nextPost: null,
					relatedPosts: [],
				};
			}
			const p = posts[currentIdx];
			const prev = currentIdx > 0 ? posts[currentIdx - 1] : null;
			const next = currentIdx < posts.length - 1 ? posts[currentIdx + 1] : null;
			// Find related posts (e.g., based on tags, excluding current) - simple version for now
			const related = posts
				.filter(
					(rp) =>
						rp.id.toString() !== id &&
						(rp.tags || []).some((tag) => (p.tags || []).includes(tag))
				)
				.slice(0, 3);
			// If not enough tag-related, fill with recent posts (excluding current, prev, next)
			if (related.length < 3) {
				const recent = posts
					.filter(
						(rp) =>
							rp.id.toString() !== id &&
							rp.id !== prev?.id &&
							rp.id !== next?.id
					)
					.slice(0, 3 - related.length);
				related.push(...recent);
			}

			return {
				post: p,
				currentIndex: currentIdx,
				prevPost: prev,
				nextPost: next,
				relatedPosts: related.slice(0, 3),
			}; // Ensure max 3 related
		}, [id]); // Depend only on ID
	console.log(post);
	// --- Format Date ---
	const formattedDate = useMemo(
		() =>
			new Date(post.date).toLocaleDateString("en-US", {
				year: "numeric",
				month: "long",
				day: "numeric",
			}),
		[post.date]
	);

	// --- Loading / Not Found State ---
	if (!posts) {
		// Check if posts array exists (useful if fetched async)
		return (
			<div className="min-h-screen flex items-center justify-center">
				<LoadingSpinner text="Loading Post..." />
			</div>
		);
	}
	if (!post) {
		// Improved Not Found State
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center text-center py-24 px-4">
				<div>
					<AlertCircle size={48} className="mx-auto text-red-400 mb-4" />
					<h2 className="text-2xl font-bold text-gray-900 mb-3">
						Post Not Found
					</h2>
					<p className="text-gray-600 mb-6">
						Sorry, the article you're looking for might have been moved or
						deleted.
					</p>
					<button
						onClick={() => navigate(-1)}
						className="btn btn-secondary-outline mr-4"
					>
						{" "}
						{/* Go Back */}
						<ArrowLeft className="w-5 h-5 mr-2" /> Go Back
					</button>
					<Link to="/blog" className="btn btn-primary">
						{" "}
						{/* Back to Blog */}
						View All Articles
					</Link>
				</div>
			</div>
		);
	}

	const postUrl = window.location.href; // Get current URL for sharing

	return (
		<>
			<Helmet>
				<title>{post.title} | Off Cut Shades Blog</title> {/* SEO Title */}
				<meta name="description" content={post.text.substring(0, 160)} />{" "}
				{/* Use excerpt for description */}
				<link rel="canonical" href={`https://offcutshades.com/blog/${id}`} />
				{/* Add OpenGraph meta tags for better sharing */}
				<meta
					property="og:title"
					content={`${post.title} | Off Cut Shades Blog`}
				/>
				<meta property="og:description" content={post.text.substring(0, 160)} />
				<meta
					property="og:image"
					content={`https://offcutshades.com${post.image}`}
				/>
				<meta
					property="og:url"
					content={`https://offcutshades.com/blog/${id}`}
				/>
				<meta property="og:type" content="article" />
				{/* Add Twitter card meta tags */}
				<meta name="twitter:card" content="summary_large_image" />
				<meta
					name="twitter:title"
					content={`${post.title} | Off Cut Shades Blog`}
				/>
				<meta
					name="twitter:description"
					content={post.text.substring(0, 160)}
				/>
				<meta
					name="twitter:image"
					content={`https://offcutshades.com${post.image}`}
				/>
			</Helmet>

			<main className="min-h-screen bg-gray-50 pt-24 pb-16 md:pt-32 md:pb-24">
				{" "}
				{/* Added top padding */}
				{/* --- Post Header --- */}
				<div className="container mx-auto px-4 mb-8 md:mb-12">
					{/* Back to Blog Link */}
					<Link
						to="/blog"
						className="text-sm text-gray-600 hover:text-primary inline-flex items-center gap-1 mb-6 transition-colors"
					>
						<ArrowLeft className="w-4 h-4" /> Back to All Articles
					</Link>
					{/* Title & Meta */}
					<motion.div
						initial={{ opacity: 0, y: 15 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5 }}
					>
						{/* Optional Category/Tags */}
						{post.tags && post.tags.length > 0 && (
							<div className="flex flex-wrap gap-2 mb-3">
								{post.tags.map((tag) => (
									<span
										key={tag}
										className="px-2.5 py-0.5 bg-primary/10 text-primary rounded-full text-xs font-medium"
									>
										{tag}
									</span>
								))}
							</div>
						)}
						<h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
							{post.title}
						</h1>
						<div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-gray-500">
							<div className="flex items-center gap-1.5">
								<Calendar size={15} />{" "}
								<time dateTime={post.date}>{formattedDate}</time>
							</div>
							<div className="flex items-center gap-1.5">
								<Clock size={15} /> <span>{post.readTime || "5 min read"}</span>
							</div>
							{/* Optional Author */}
							{/* <div className="flex items-center gap-1.5"><User size={15} /> <span>By {post.author || 'Off Cut Shades Team'}</span></div> */}
						</div>
					</motion.div>
				</div>
				{/* --- Content Section (Main + Sidebar) --- */}
				<div className="container mx-auto px-4">
					{" "}
					{/* Slightly wider container */}
					<div className="grid grid-cols-1 lg:grid-cols-[minmax(0,3fr)_minmax(0,1fr)] gap-10 md:gap-12 items-start">
						{" "}
						{/* Main content wider */}
						{/* Main Content Column */}
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ duration: 0.6, delay: 0.2 }}
							className="lg:col-span-1" // Takes 3 parts of the grid space
						>
							<article className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8 lg:p-10">
								{/* Ensure prose styles provide good readability */}
								<div className="prose prose-lg prose-gray max-w-none prose-headings:font-semibold prose-a:text-primary hover:prose-a:underline prose-img:rounded-lg prose-img:shadow-sm prose-img:border">
									<MarkdownPost content={post.text} />
								</div>
							</article>

							{/* Share Buttons (after content) */}
							<div className="mt-8 pt-6 border-t border-gray-200 flex items-center justify-center md:justify-start">
								<SocialShare url={postUrl} title={post.title} />
							</div>

							{/* Post Navigation (Prev/Next) */}
							<nav
								className="mt-10 md:mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4"
								aria-label="Blog post navigation"
							>
								{prevPost ? (
									<Link
										to={`/blog/${prevPost.id}`}
										className="group p-4 bg-white rounded-lg shadow-sm border border-gray-200 hover:border-primary/30 transition-colors text-left"
									>
										<div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
											{" "}
											<ArrowLeft size={14} /> Previous Post{" "}
										</div>
										<h3 className="font-medium text-sm text-gray-800 group-hover:text-primary transition-colors line-clamp-2">
											{prevPost.title}
										</h3>
									</Link>
								) : (
									<div></div>
								)}{" "}
								{/* Placeholder for grid */}
								{nextPost ? (
									<Link
										to={`/blog/${nextPost.id}`}
										className="group p-4 bg-white rounded-lg shadow-sm border border-gray-200 hover:border-primary/30 transition-colors text-right"
									>
										<div className="flex items-center justify-end gap-2 text-gray-500 text-xs mb-1">
											{" "}
											Next Post <ArrowRight size={14} />{" "}
										</div>
										<h3 className="font-medium text-sm text-gray-800 group-hover:text-primary transition-colors line-clamp-2">
											{nextPost.title}
										</h3>
									</Link>
								) : (
									<div></div>
								)}{" "}
								{/* Placeholder for grid */}
							</nav>
						</motion.div>
						{/* Sidebar Column */}
						<motion.aside
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ duration: 0.6, delay: 0.3 }}
							className="lg:sticky lg:top-28 space-y-8" // Make sidebar sticky
						>
							{/* Optional: Table of Contents */}
							{/* Add TOC component here if implemented */}

							{/* Related Posts */}
							{relatedPosts.length > 0 && (
								<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
									<h3 className="font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-100 text-base">
										Related Articles
									</h3>
									<div className="space-y-4">
										{relatedPosts.map((related) => (
											<RelatedPostCard key={related.id} post={related} />
										))}
									</div>
								</div>
							)}

							{/* Optional: Relevant CTA */}
							<div className="bg-primary/10 rounded-xl border border-primary/20 p-5 text-center">
								<h4 className="font-semibold text-gray-800 mb-2">
									Ready for the Perfect Shade?
								</h4>
								<p className="text-sm text-gray-600 mb-4">
									Explore our collection or get free samples sent to your home.
								</p>
								<div className="flex flex-col sm:flex-row lg:flex-col gap-3">
									<Link
										to="/shop"
										className="btn btn-primary text-sm py-2 flex-1"
									>
										Shop Shades
									</Link>
									<Link
										to="/swatches"
										className="btn btn-secondary-outline text-sm py-2 flex-1"
									>
										Get Samples
									</Link>
								</div>
							</div>
						</motion.aside>
					</div>
				</div>
			</main>
		</>
	);
}

// Add button styles if needed
/*
@layer components {
  .btn { @apply px-5 py-2.5 rounded-lg font-semibold text-center shadow-sm transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed; }
  .btn-primary { @apply bg-primary text-white hover:bg-primary/90; }
  .btn-secondary-outline { @apply bg-white text-gray-700 border border-gray-300 hover:border-gray-400 hover:bg-gray-50; }
}
*/
